import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import type { OnDestroy } from '@angular/core'
import { Component, DestroyRef, effect, inject, Injector, signal, ViewContainerRef } from '@angular/core'
import {
  NavigationCancel,
  NavigationCancellationCode,
  Router,
  NavigationEnd,
  NavigationError,
  RouterOutlet
} from '@angular/router'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { environment } from '@awork/environments/environment'
import { maskUrl } from '@awork/_shared/functions/url-helpers'
import { PlanService } from '@awork/_shared/services/plan-service/plan.service'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import { getContext, initialize, registerOnThemeChangeHandler } from '@microsoft/teams-js'
import { AppQuery } from '@awork/core/state/app.query'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { filter, take, tap } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DesktopService } from '@awork/_shared/services/desktop-service/desktop.service'
import { DynamicRefService } from './_shared/services/dynamic-ref-service/dynamic-ref.service'
import { ToastService } from './_shared/services/toast-service/toast.service'
import { ModalService } from './_shared/services/modal-service/modal.service'
import { SubscriptionQuery } from '@awork/_shared/state/subscription.query'
import { PlanStatus } from '@awork/_shared/models/subscription.types'
import { toObservable } from '@angular/core/rxjs-interop'
import { FeatureFlagIntegrationService } from '@awork/_shared/services/feature-flag-integration-service/feature-flag-integration.service'
import { ThemeService } from '@awork/_shared/services/theme-service/theme.service'
import { SupportDiagnosticsService } from './_shared/services/support-diagnostics-service/support-diagnostics.service'

@Component({
  selector: 'aw-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet]
})
export class AppComponent implements OnDestroy {
  private viewRef = inject(ViewContainerRef)
  private dynamicRefService = inject(DynamicRefService)
  private router = inject(Router)
  private modalService = inject(ModalService)
  private toastService = inject(ToastService)
  private trackingService = inject(TrackingService)
  private planService = inject(PlanService)
  private accountService = inject(AccountService)
  private browserService = inject(BrowserService)
  private appQuery = inject(AppQuery)
  private logService = inject(LogService)
  private desktopService = inject(DesktopService)
  private settingsQuery = inject(SettingsQuery)
  private subscriptionQuery = inject(SubscriptionQuery)
  private themeService = inject(ThemeService)
  private injector = inject(Injector)
  private destroyRef = inject(DestroyRef)
  private signalStoreService = inject(SignalStoreService)
  private bootLoaderTimeoutId: number | null = null
  private bootFailureHandled = false
  private lastRouterEvent = 'none'
  private navigationEndCount = 0
  private loaderRemovalWaits = 0
  private lastCheckingVersionSeen: boolean | null = null

  public lastRoute: string
  readonly isMSTeams = signal(false)

  private loaderTimeoutId: ReturnType<typeof setTimeout> | undefined

  // the viewRef is necessary to have the root ref available in other components
  // this is for creating notifications, modals, etc.
  constructor() {
    document.body.classList.add(this.browserService.isMobilePhone ? 'aw-mobile' : 'aw-web')

    this.dynamicRefService.appViewRef = this.viewRef
    this.destroyRef.onDestroy(() => {
      this.dynamicRefService.appViewRef = null
      this.cancelBootLoaderWatchdog()
    })
    this.logService.initSentry()
    this.logService.initDatadogLogs()
    this.logService.initDatadogRum()
    inject(FeatureFlagIntegrationService)
    inject(SupportDiagnosticsService)

    effect(() => {
      const isInMSTeams = this.appQuery.query('isMSTeams')

      if (isInMSTeams()) {
        this.isMSTeams.set(true)
        this.initTeams()
      }
    })

    // some global router event subscriptions
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      this.lastRouterEvent = `${event.type}:${'id' in event ? event.id : ''}`

      // Store the attempted URL by subscribing to the NavigationCancel event
      if (event instanceof NavigationCancel) {
        // Saving URL from canceled route.
        this.accountService.setRedirectUrl(event.url)

        if (event.code === NavigationCancellationCode.NoDataFromResolver) {
          this.handleFailedNavigation()
        }
      }

      if (event instanceof NavigationError) {
        this.handleFailedNavigation()
      }

      // Store the last route in the navigation service
      if (event instanceof NavigationEnd) {
        this.navigationEndCount++
        this.removeLoader()

        // Track page views
        // track event is for mixpanel (page is being ignored)
        this.trackingService.trackEvent(TrackingEvent.pageViewed, {
          url: maskUrl(event.url),
          is_paid:
            this.subscriptionQuery.isPaidPlan() &&
            this.subscriptionQuery.getSubscription().isPlanStatus(PlanStatus.InTrial),
          integrations: {
            All: true,
            'Google Analytics': false,
            'Facebook Pixel': false
          },
          screen_width: window.innerWidth,
          embedded_in: this.getEmbeddedIn()
        })

        // Track the page explicitly for other segment destinations
        this.trackingService.trackPage(maskUrl(event.url))

        // Hide all modals on navigating
        this.modalService.hideAllModals()

        // hide the purple background if framework module is shown
        // and show it again if it's being removed
        // delay needed because the id is set by hostbinding
        clearTimeout(this.loaderTimeoutId)
        this.loaderTimeoutId = setTimeout(() => {
          this.loaderTimeoutId = undefined

          if (this.destroyRef.destroyed) {
            return
          }

          if (document?.getElementById('aw-lyt')) {
            if (!document.body.className.includes('rdy')) {
              document.body.classList.add('rdy')
            }
          } else {
            if (document.body?.className?.includes('rdy')) {
              document.body.classList.remove('rdy')
            }
          }
        })
      }
    })

    // A navigation can finish before this component exists, for example when a service navigates
    // during bootstrap. The router then skips its first navigation and no NavigationEnd arrives here.
    if (this.router.lastSuccessfulNavigation()) {
      this.removeLoader()
    }

    this.startBootLoaderWatchdog()

    this.awConsole()
    effect(() => this.themeService.setDarkMode())

    this.initDesktopApp()
    this.checkExtendTrialStatus()
  }

  /**
   * Cancels the pending loader-background update.
   */
  ngOnDestroy(): void {
    clearTimeout(this.loaderTimeoutId)
    this.cancelBootLoaderWatchdog()
  }

  /**
   * Gets the embedded in key string for tracking
   * @returns {string}
   */
  private getEmbeddedIn(): string {
    let embeddedIn = 'default'

    if (this.isMSTeams()) {
      return (embeddedIn = 'ms-teams')
    }

    if (this.desktopService.isDesktop) {
      switch (this.browserService.getOS()) {
        case 'Mac':
          embeddedIn = 'electron-osx'
          break
        case 'Windows':
          embeddedIn = 'electron-win'
          break
      }
    }

    return embeddedIn
  }

  /**
   * Initializes MS Teams SDK
   */
  private initTeams(): void {
    initialize()

    // register handler if the ms team theme is changed
    registerOnThemeChangeHandler(theme => {
      this.themeService.setDarkMode(theme)
    })

    // override current mode if it is in ms teams
    getContext(context => {
      this.themeService.setDarkMode(context.theme)
    })
  }

  /**
   * You know what it is ;)
   */
  private awConsole(): void {
    if (environment !== 'local') {
      console.log(
        `
                          _
                         | |
  __ ___      _____  _ __| | __
 / _' \\ \\ /\\ / / _ \\| '__| |/ /
| (_| |\\ V  V / (_) | |  |   <
 \\__,_| \\_/\\_/ \\___/|_|  |_|\\_\\


Nothing to see here! Stay productive ;)
`
      )
    }
  }

  /**
   * Removes the App's loader from the DOM
   * @private
   */
  private removeLoader(): void {
    this.loaderRemovalWaits++

    toObservable(this.appQuery.query('checkingVersion'), { injector: this.injector })
      .pipe(
        tap(checking => (this.lastCheckingVersionSeen = checking)),
        filter(checking => !checking),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.removeLoaderElement())
  }

  /**
   * Removes the static startup loader without waiting for router state.
   */
  private removeLoaderElement(): void {
    this.cancelBootLoaderWatchdog()
    document.getElementById('loader')?.remove()
  }

  /**
   * Recovers from a failed initial navigation and offers a reload when the session is still active.
   */
  private handleFailedNavigation(): void {
    if (this.bootFailureHandled || !document.getElementById('loader')) {
      return
    }

    this.bootFailureHandled = true
    this.cancelBootLoaderWatchdog()
    this.removeLoaderElement()

    if (!this.appQuery.getIsLoggedIn()) {
      this.router.navigateByUrl('/login')

      return
    }

    this.toastService.show(q.translations.errors.somethingWentWrong, {
      type: 'error',
      action: () => window.location.reload(),
      actionText: q.translations.AppErrorHandler.reloadPage
    })
  }

  /**
   * Starts one watchdog for a startup loader that remains visible too long.
   */
  private startBootLoaderWatchdog(): void {
    if (!document.getElementById('loader')) {
      return
    }

    this.bootLoaderTimeoutId = window.setTimeout(() => {
      this.bootLoaderTimeoutId = null

      if (!document.getElementById('loader')) {
        return
      }

      this.logStartupLoaderWatchdog()
      this.handleFailedNavigation()
    }, 30_000)
  }

  /**
   * Logs the startup state when the loader watchdog fires, to find why the normal removal did not run.
   */
  private logStartupLoaderWatchdog(): void {
    const checkingVersionStore = this.appQuery.query('checkingVersion')
    const appStoreRestored = this.signalStoreService.queryOnStoreRestored('app')
    const storageStatus = this.signalStoreService.getStorageStatus()

    this.logService.logMessage(Level.warn, 'Startup loader watchdog fired', {
      routerUrl: maskUrl(this.router.url),
      routerNavigated: this.router.navigated,
      lastRouterEvent: this.lastRouterEvent,
      navigationEndCount: this.navigationEndCount,
      loaderRemovalWaits: this.loaderRemovalWaits,
      lastCheckingVersionSeen: String(this.lastCheckingVersionSeen),
      checkingVersionStore: checkingVersionStore(),
      storageInitialized: storageStatus.initialized,
      persistenceEnabled: storageStatus.enabled,
      appStoreRestored: appStoreRestored(),
      pendingPersistedStores: this.signalStoreService.getPendingPersistedStoreNames(),
      refreshingSession: this.accountService.refreshingSession$.value,
      isLoggedIn: this.appQuery.getIsLoggedIn(),
      visibilityState: document.visibilityState,
      serviceWorkerController: Boolean(navigator.serviceWorker?.controller),
      renderedInputs: document.querySelectorAll('input').length
    })
  }

  /**
   * Cancels the startup watchdog after the loader is removed.
   */
  private cancelBootLoaderWatchdog(): void {
    if (this.bootLoaderTimeoutId === null) {
      return
    }

    window.clearTimeout(this.bootLoaderTimeoutId)
    this.bootLoaderTimeoutId = null
  }

  /**
   * Initializes the desktop app if the user has the desktop app installed
   * If there is no setting stored yet, but running in the desktop app, we
   * set the setting to true
   */
  private initDesktopApp(): void {
    if (!this.browserService.isMobilePhone) {
      this.desktopService.initialize(this.settingsQuery.getDesktopAppSettings()?.openLinksInDesktopApp)
    }
  }

  /**
   * Checks if the trial got extended to show a toast message
   */
  private checkExtendTrialStatus(): void {
    this.planService.extendTrialStatus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      if (status === 'success') {
        this.toastService.show(q.translations.FrameworkComponent.extendedTrialSuccessful)
      } else {
        this.toastService.show(q.translations.FrameworkComponent.extendedTrialError, { type: 'error' })
      }
    })
  }
}
